import { LanguageLevelsOptions } from 'api/types/ReferenceData';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import ErrorMessage from 'components/common/ErrorMessage';
import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  IconButton,
  Box
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { MuiAsyncSingleSelect } from 'components/common/Select/MUIAsyncSelects/MuiAsyncSingleSelect';
import { useGetLanguages } from 'api/services/UserServiceTS';
import { useCallback } from 'react';
import { Option } from 'types/global';

interface IUserLanguages {
  languagesOptions: any;
}

export const UserLanguages = ({ languagesOptions }: IUserLanguages) => {
  const [, getLanguages] = useGetLanguages(0, '');
  const {
    control,
    formState: { errors },
    getValues
  } = useFormContext();

  const { fields, append, remove } = useFieldArray({
    control: control,
    name: 'languages'
  });
  const newLanguage = {
    name: null,
    level: null
  };
  const getOptions = useCallback(
    (page: number, search: string) =>
      new Promise((resolve, reject) => {
        getLanguages({
          params: {
            page,
            name__icontains: search || null
          }
        })
          .then(res => {
            const selectedLanguages = getValues().languages.map(
              (language: null | { name: Option }) => language?.name?.label
            );
            const availableLanguages = res.data.content.filter(
              (c: { name: string }) => !selectedLanguages.includes(c.name)
            );
            resolve(
              availableLanguages.map((c: { name: string; key: string }) => ({
                label: c.name,
                value: c.key
              }))
            );
          })
          .catch(() => {
            reject();
          });
      }),
    [getLanguages, getValues]
  );

  return (
    <div>
      <FormControl fullWidth>
        <Box sx={{ display: 'flex', gap: 3, flexDirection: 'column' }}>
          {fields &&
            fields.map((field: any, index: number) => (
              <Box sx={{ display: 'flex', gap: 3 }} key={field?.id}>
                <div style={{ minWidth: '8rem', width: '100%' }}>
                  <Controller
                    control={control}
                    name={`languages.${index}.name` as any}
                    rules={{ required: true }}
                    render={({ field: f }) => {
                      let propsValue = f.value?.value ? f.value : null;
                      if (
                        f?.value?.label !== undefined &&
                        f?.value?.value === undefined
                      ) {
                        propsValue = {
                          value: languagesOptions.find(
                            (lang: { value: string; name: string }) =>
                              lang.name === f.value.label
                          ),
                          label: f.value.label
                        };
                      }

                      return (
                        <Box sx={{ my: -2 }}>
                          <MuiAsyncSingleSelect
                            {...f}
                            label={'Select language'}
                            getOption={getOptions}
                            propsValue={propsValue}
                          />
                        </Box>
                      );
                    }}
                  />

                  {
                    //@ts-ignore TODO: needs User and Form Types
                    errors?.languages?.[index]?.name?.type === 'required' && (
                      <Box sx={{ mt: 1 }}>
                        <ErrorMessage type="required" />
                      </Box>
                    )
                  }
                </div>

                <div style={{ minWidth: '8rem', width: '100%' }}>
                  <Controller
                    control={control}
                    name={`languages.${index}.level` as any}
                    rules={{ required: true }}
                    render={({ field: controlerField }) => (
                      <FormControl fullWidth>
                        <InputLabel>Select proficiency level</InputLabel>
                        <Select
                          label="Select proficiency level"
                          {...controlerField}
                          value={controlerField.value?.value}
                          onChange={e => {
                            controlerField.onChange({
                              value: e.target.value
                            });
                          }}
                        >
                          {LanguageLevelsOptions.map((level: any) => (
                            <MenuItem key={level.value} value={level.value}>
                              {level.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />

                  {
                    //@ts-ignore TODO: needs User and Form Types
                    errors?.languages?.[index]?.level?.type === 'required' && (
                      <ErrorMessage type="required" />
                    )
                  }
                </div>
                <Box sx={{ mt: 1 }}>
                  <IconButton
                    color="secondary"
                    onClick={() => remove(index)}
                    aria-label="delete"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>
            ))}
        </Box>
      </FormControl>

      <div>
        <Button
          variant="contained"
          color="primary"
          onClick={() => append(newLanguage)}
          sx={{ marginTop: fields.length > 0 ? 2 : 0 }}
        >
          Add language
        </Button>
      </div>
    </div>
  );
};
